// The chat screen auto-sends a `prompt` route param once. A ref on the
// component only survives as long as the mount: opening the CV editor from a
// tool card and pressing Back returns to the same URL, remounts the thread, and
// the prompt is sent to the agent a second time - burning the user's AI
// allowance for a message they never wrote (issue #268).
//
// The guard therefore lives outside React and the caller drops the param from
// the route once it has been consumed. It is keyed by the navigation that
// carried the prompt (`promptId`), NOT by the prompt text: the Enhance docks
// send the same default prompt every time, so a text-keyed guard silently
// swallowed every press after the first one of the app session - the chat
// opened empty and nothing happened. A remount replays the same `promptId` and
// is still blocked; a fresh press mints a new one and goes through.

const sentPrompts = new Set<string>();

export interface InitialPromptScope {
  threadId?: string | null;
  /** Unique per navigation - see `buildChatPromptParams`. */
  promptId?: string | null;
}

function key(prompt: string, scope?: InitialPromptScope) {
  const promptId = scope?.promptId?.trim();
  return `${scope?.threadId ?? 'new'}::${promptId || prompt}`;
}

export function normalizeInitialPrompt(value?: string | string[] | null) {
  const raw = Array.isArray(value) ? value[0] : value;
  const trimmed = raw?.trim();
  return trimmed ? trimmed : null;
}

/** Identifies one prompt navigation, so a remount of it cannot resend. */
export function createChatPromptId() {
  return `p${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Route params for "hand this prompt to the agent chat". Always navigate with
 * this rather than a bare `{ prompt }` so repeat sends of an identical prompt
 * are not mistaken for a remount.
 */
export function buildChatPromptParams(prompt: string) {
  return { prompt, promptId: createChatPromptId() };
}

export function shouldSendInitialPrompt(
  prompt?: string | null,
  scope?: InitialPromptScope,
) {
  if (!prompt) return false;
  return !sentPrompts.has(key(prompt, scope));
}

export function markInitialPromptSent(
  prompt: string,
  scope?: InitialPromptScope,
) {
  sentPrompts.add(key(prompt, scope));
}

/** Test-only: the guard is process-wide by design. */
export function resetInitialPromptGuard() {
  sentPrompts.clear();
}
